import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { User, Mail, Phone, MapPin, Info } from "lucide-react";
import { useCountry } from "@/contexts/country";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CountryFlag from "@/components/country/CountryFlag";
import AvatarUpload from "./AvatarUpload";

const ProfileForm = () => {
  const { user, profile, updateProfile } = useAuth();
  const { availableCountries, availableConstituencies } = useCountry();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_number: "",
    country_code: "",
    constituency_code: "",
  });

  // Enhanced logging for debugging
  useEffect(() => {
    console.log("=== ProfileForm Debug Info ===");
    console.log("Profile:", profile);
    console.log("Available countries count:", availableCountries.length);
    console.log(
      "Available constituencies count:",
      availableConstituencies.length
    );
    console.log("Current form data:", formData);

    if (profile) {
      console.log("Profile country_code:", profile.country_code);
      console.log("Profile constituency_code:", profile.constituency_code);

      const filteredConstituencies = availableConstituencies.filter(
        (constituency) => constituency.country_code === profile.country_code
      );
      console.log(
        "Filtered constituencies for profile country:",
        filteredConstituencies
      );

      const matchingConstituency = filteredConstituencies.find(
        (constituency) => constituency.code === profile.constituency_code
      );
      console.log("Matching constituency found:", matchingConstituency);
    }
    console.log("===============================");
  }, [profile, availableCountries, availableConstituencies, formData]);

  // Main effect to populate form data from profile
  useEffect(() => {
    if (profile) {
      console.log("Setting form data from profile...");
      console.log("Profile data being set:", {
        name: profile.name || "",
        email: profile.email || "",
        phone_number: profile.phone_number || "",
        country_code: profile.country_code || "",
        constituency_code: profile.constituency_code || "",
      });

      setFormData({
        name: profile.name || "",
        email: profile.email || "",
        phone_number: profile.phone_number || "",
        country_code: profile.country_code || "",
        constituency_code: profile.constituency_code || "",
      });
    } else {
      console.log("Not setting form data: no profile");
    }
  }, [profile]); // Removed availableConstituencies dependency

  // Separate effect to handle constituency validation
  useEffect(() => {
    if (
      profile?.constituency_code &&
      availableConstituencies.length > 0 &&
      formData.constituency_code !== profile.constituency_code
    ) {
      console.log("Constituency mismatch detected, correcting form data");
      console.log("Profile constituency:", profile.constituency_code);
      console.log("Form constituency:", formData.constituency_code);

      // Check if the profile's constituency exists in available constituencies
      const constituencyExists = availableConstituencies.find(
        (constituency) => constituency.code === profile.constituency_code
      );

      if (constituencyExists) {
        console.log("Constituency exists, updating form data");
        setFormData((prev) => ({
          ...prev,
          constituency_code: profile.constituency_code || "",
        }));
      } else {
        console.log("Constituency does not exist in available list");
      }
    }
  }, [
    profile?.constituency_code,
    availableConstituencies,
    formData.constituency_code,
  ]);

  // Additional effect to monitor when constituency data becomes available
  useEffect(() => {
    console.log("Constituencies changed:", {
      count: availableConstituencies.length,
      profileConstituency: profile?.constituency_code,
      formConstituency: formData.constituency_code,
    });
  }, [
    availableConstituencies,
    profile?.constituency_code,
    formData.constituency_code,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log("Submitting form data:", formData);
      await updateProfile(formData);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    console.log(`Updating field ${field} with value:`, value);
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Clear constituency when country changes
      if (field === "country_code" && value !== prev.country_code) {
        newData.constituency_code = "";
        console.log("Country changed, clearing constituency");
      }

      console.log("New form data:", newData);
      return newData;
    });
  };

  // Filter constituencies based on selected country
  const filteredConstituencies = availableConstituencies.filter(
    (constituency) => constituency.country_code === formData.country_code
  );

  console.log("Render - Current form data:", formData);
  console.log("Render - Filtered constituencies:", filteredConstituencies);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <AvatarUpload />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="Enter your email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  value={formData.phone_number}
                  onChange={(e) =>
                    handleInputChange("phone_number", e.target.value)
                  }
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country" className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  Default Country
                </Label>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>
                    This is your primary country for viewing polls and content.
                    You can verify additional countries separately.
                  </span>
                </div>
                <Select
                  value={formData.country_code}
                  onValueChange={(value) =>
                    handleInputChange("country_code", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCountries.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        <div className="flex items-center gap-2">
                          <CountryFlag
                            countryCode={country.code}
                            className="h-4 w-5"
                          />
                          <span>{country.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="constituency">Constituency</Label>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>
                    Your local electoral district or voting area within your
                    selected country. This helps us show you relevant local
                    polls and content.
                  </span>
                </div>
                <Select
                  value={formData.constituency_code}
                  onValueChange={(value) =>
                    handleInputChange("constituency_code", value)
                  }
                  disabled={!formData.country_code}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        !formData.country_code
                          ? "Select country first"
                          : filteredConstituencies.length === 0
                          ? "No constituencies available"
                          : formData.constituency_code
                          ? `Selected: ${
                              filteredConstituencies.find(
                                (c) => c.code === formData.constituency_code
                              )?.name || "Unknown"
                            }`
                          : "Select constituency"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredConstituencies.map((constituency) => (
                      <SelectItem
                        key={constituency.id}
                        value={constituency.code}
                      >
                        {constituency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.country_code &&
                  filteredConstituencies.length === 0 && (
                    <div className="text-sm text-amber-600 dark:text-amber-400">
                      No constituencies are available for the selected country.
                    </div>
                  )}
                {/* Debug info display */}
                {/*<div className="text-xs text-gray-500 mt-1">
                  Debug: Country={formData.country_code}, Constituency={formData.constituency_code}, 
                  Available={filteredConstituencies.length}
                </div>*/}
              </div>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={isLoading}
                variant="purple"
                className="mt-4"
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileForm;
